import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { CalendarToolbar } from "@/components/calendar/CalendarToolbar";
import { EventDetailModal } from "@/components/calendar/EventDetailModal";
import { ConflictsPanel } from "@/components/calendar/ConflictsPanel";
import { ClusterPickerDialog } from "@/components/calendar/ClusterPickerDialog";
import { ExtendReleaseDialog } from "@/components/calendar/ExtendReleaseDialog";
import {
  PICKUP_COLOR, DELIVERY_COLOR, INV_PICKUP_COLOR, INV_DELIVERY_COLOR,
  SPAN_COLOR, SPAN_INV_COLOR, RELEASE_DUE_COLOR,
  buildSpanEvents, buildPointEvents,
} from "@/components/calendar/eventBuilders";

// Stable references for FullCalendar props — avoids re-renders triggered by inline objects
const FC_PLUGINS = [dayGridPlugin, timeGridPlugin, interactionPlugin];
const FC_EVENT_TIME_FORMAT = { hour: "2-digit", minute: "2-digit", meridiem: false };
const FC_BUTTON_TEXT = { today: "Oggi", month: "Mese", week: "Settimana" };

export default function Calendar() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const [currentView, setCurrentView] = useState(isMobile ? "timeGridDay" : "dayGridMonth");
  const [title, setTitle] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clusterPicker, setClusterPicker] = useState(null);
  const [extendRelease, setExtendRelease] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);
  const [displayMode, setDisplayMode] = useState(() => {
    if (typeof window === "undefined") return "points";
    return localStorage.getItem("calendar_display_mode") || "points";
  });
  const [onlyMine, setOnlyMine] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("calendar_only_mine") === "true";
  });
  const [operatorFilter, setOperatorFilter] = useState(() => {
    if (typeof window === "undefined") return "all";
    return localStorage.getItem("calendar_operator_filter") || "all";
  });
  const canEdit = hasPermission("quotes.edit_all");

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/events");
      setRawEvents(data.events || []);
    } catch {
      toast.error("Errore nel caricamento degli eventi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const events = useMemo(
    () => (displayMode === "span" ? buildSpanEvents(rawEvents) : buildPointEvents(rawEvents)),
    [rawEvents, displayMode],
  );

  const allConflictDocs = useMemo(() => {
    if (displayMode !== "span") return [];
    const map = new Map();
    events.filter((e) => e.extendedProps?.hasConflict).forEach((e) => {
      const k = `${e.extendedProps.doc_type}_${e.extendedProps.doc_id}`;
      if (map.has(k)) return;
      map.set(k, {
        key: k,
        doc_id: e.extendedProps.doc_id,
        doc_type: e.extendedProps.doc_type,
        number: e.extendedProps.number,
        customer_name: e.extendedProps.customer_name,
        assigned_to: e.extendedProps.assigned_to,
        assigned_to_name: e.extendedProps.assigned_to_name,
        start: e.extendedProps.spanStart,
        end: e.extendedProps.spanEnd,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.start.localeCompare(b.start));
  }, [events, displayMode]);

  const operatorOptions = useMemo(() => Array.from(
    allConflictDocs.reduce((map, d) => {
      const key = d.assigned_to || "__unassigned__";
      if (!map.has(key)) {
        map.set(key, { value: key, label: d.assigned_to ? (d.assigned_to_name || "—") : "Non assegnato" });
      }
      return map;
    }, new Map()).values()
  ).sort((a, b) => a.label.localeCompare(b.label)), [allConflictDocs]);

  const conflictDocs = useMemo(() => {
    let list = allConflictDocs;
    if (operatorFilter !== "all") {
      list = list.filter((d) => operatorFilter === "__unassigned__" ? !d.assigned_to : d.assigned_to === operatorFilter);
    }
    if (onlyMine && user) list = list.filter((d) => d.assigned_to === user.id);
    return list;
  }, [allConflictDocs, operatorFilter, onlyMine, user]);

  const hiddenByFilter = allConflictDocs.length - conflictDocs.length;

  const openConflictDoc = (doc) => {
    navigate(doc.doc_type === "inventory" ? `/inventories/${doc.doc_id}` : `/quotes/${doc.doc_id}`);
  };

  const toggleDisplayMode = () => {
    const next = displayMode === "points" ? "span" : "points";
    setDisplayMode(next);
    localStorage.setItem("calendar_display_mode", next);
  };

  const handleEventDrop = async (info) => {
    if (!canEdit) { info.revert(); toast.error("Non hai i permessi per spostare eventi"); return; }
    const { doc_id, doc_type, type } = info.event.extendedProps;
    const docId = doc_id || info.event.extendedProps.quote_id;
    const newDate = info.event.startStr;

    // Release_due: open confirmation dialog with extension calculation
    if (type === "release_due") {
      const oldDate = info.event.extendedProps.date || info.oldEvent.startStr;
      // Try to fetch monthly_cost from the linked quote (release proforma has monthly=0 by design)
      let monthlyCost = 0;
      try {
        const pfRes = await api.get(`/proformas/${docId}`);
        const qid = pfRes.data?.quote_id;
        if (qid) {
          const qRes = await api.get(`/quotes/${qid}`);
          monthlyCost = qRes.data?.storage_monthly_cost || 0;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[Calendar] Impossibile recuperare il monthly_cost del preventivo per il dialog estensione:", err?.message);
        }
      }
      setExtendRelease({ proformaId: docId, oldDate, newDate, monthlyCost, info });
      return;
    }

    // Validation for quote and inventory events: pickup must be <= delivery
    if (doc_type === "quote" || doc_type === "inventory") {
      const sibling = rawEvents.find(e => e.doc_id === docId && e.type !== type);
      if (sibling) {
        const siblingDate = sibling.date;
        const isDelivery = type === "delivery" || type === "inventory_delivery";
        const isPickup = type === "pickup" || type === "inventory_pickup";
        if (isDelivery && newDate < siblingDate) { info.revert(); toast.error(`La consegna non può essere prima del ritiro (${siblingDate})`); return; }
        if (isPickup && newDate > siblingDate) { info.revert(); toast.error(`Il ritiro non può essere dopo la consegna (${siblingDate})`); return; }
      }
    }

    try {
      await api.put(`/calendar/events/${docId}?event_type=${type}&new_date=${newDate}`);
      toast.success("Data aggiornata");
      fetchEvents();
    } catch (err) { info.revert(); toast.error(err.response?.data?.detail || "Errore nell'aggiornamento"); }
  };

  const confirmExtendRelease = async () => {
    if (!extendRelease) return;
    setExtendLoading(true);
    try {
      const { data } = await api.post(
        `/proformas/extend-release/${extendRelease.proformaId}`,
        { new_date: extendRelease.newDate }
      );
      if (data.extension_proforma) {
        toast.success(
          `Estensione creata (${data.calendar_months} mesi + ${data.residual_days} gg, ${data.proposed_amount.toFixed(2)} €)`,
          { action: { label: "Apri", onClick: () => navigate(`/proformas/${data.extension_proforma.id}`) } }
        );
      } else {
        toast.success("Scadenza aggiornata");
      }
      setExtendRelease(null);
      fetchEvents();
    } catch (err) {
      if (extendRelease.info) extendRelease.info.revert();
      toast.error(err.response?.data?.detail || "Errore nell'estensione");
    } finally {
      setExtendLoading(false);
    }
  };

  const cancelExtendRelease = () => {
    if (extendRelease?.info) extendRelease.info.revert();
    setExtendRelease(null);
  };

  const calApi = () => calendarRef.current?.getApi();
  const switchView = (view) => { calApi()?.changeView(view); setCurrentView(view); };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Outfit']" data-testid="calendar-title">Calendario Traslochi</h1>
          <p className="text-sm text-zinc-500 mt-1">Pianifica e visualizza ritiri e consegne</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs flex-wrap">
          <button
            type="button"
            data-testid="calendar-display-mode-toggle"
            onClick={toggleDisplayMode}
            className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors mr-3 ${displayMode === "span" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
          >
            {displayMode === "span" ? "Vista: Gantt" : "Vista: Punti"}
          </button>
          {displayMode === "points" ? (
            <>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: PICKUP_COLOR }} />
              <span className="text-zinc-600 mr-2">Ritiro</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: DELIVERY_COLOR }} />
              <span className="text-zinc-600 mr-2">Consegna</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: INV_PICKUP_COLOR }} />
              <span className="text-zinc-600 mr-2">Inv. Ritiro</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: INV_DELIVERY_COLOR }} />
              <span className="text-zinc-600 mr-2">Inv. Consegna</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: RELEASE_DUE_COLOR }} />
              <span className="text-zinc-600">Riconsegna</span>
            </>
          ) : (
            <>
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SPAN_COLOR }} />
              <span className="text-zinc-600 mr-2">Trasloco</span>
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SPAN_INV_COLOR }} />
              <span className="text-zinc-600 mr-2">Inventario</span>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: RELEASE_DUE_COLOR }} />
              <span className="text-zinc-600 mr-2">Riconsegna</span>
              <span className="w-3 h-3 rounded-sm border-2 border-[#DC2626] bg-white" />
              <span className="text-zinc-600">Date sovrapposte</span>
            </>
          )}
        </div>
      </div>

      <CalendarToolbar title={title} currentView={currentView} onPrev={() => calApi()?.prev()} onNext={() => calApi()?.next()} onToday={() => calApi()?.today()} onViewChange={switchView} />

      <div className={`grid gap-4 ${allConflictDocs.length > 0 ? "lg:grid-cols-[1fr_320px]" : ""}`}>
        <div
          className="card-surface p-2 sm:p-4 [&_.fc-popover]:bg-white [&_.fc-popover]:shadow-lg [&_.fc-popover]:rounded-sm [&_.fc-popover]:border-2 [&_.fc-popover]:border-black [&_.calendar-conflict-event]:!border-2 [&_.calendar-conflict-event]:!border-[#DC2626] [&_.calendar-conflict-event]:shadow-[0_0_0_1px_#DC2626] [&_.fc-daygrid-event-harness]:!my-[3px] [&_.fc-daygrid-event]:!rounded-[4px] [&_.fc-daygrid-event]:!shadow-sm [&_.fc-daygrid-event]:!border-0 [&_.fc-h-event_.fc-event-main]:!px-2 [&_.fc-h-event_.fc-event-main]:!py-[3px] [&_.fc-event-title]:!text-[11.5px] [&_.fc-event-title]:!font-medium [&_.fc-event-title]:!leading-tight [&_.fc-event-title]:!whitespace-nowrap [&_.fc-event-title]:!overflow-hidden [&_.fc-event-title]:!text-ellipsis [&_.fc-daygrid-day-number]:!text-xs [&_.fc-daygrid-day-number]:!text-zinc-400 [&_.fc-daygrid-day-top]:!justify-end"
          data-testid="calendar-container"
        >
        <FullCalendar
          ref={calendarRef}
          plugins={FC_PLUGINS}
          initialView={isMobile ? "timeGridDay" : "dayGridMonth"}
          locale="it" firstDay={1} headerToolbar={false}
          events={events} editable={canEdit}
          eventDrop={handleEventDrop}
          eventClick={(info) => {
            const props = info.event.extendedProps;
            const cluster = props.clusterDocs || [];
            if (cluster.length > 1) {
              setClusterPicker({ docs: cluster, title: info.event.title, customer_name: props.customer_name });
            } else {
              setSelectedEvent(props);
            }
          }}
          eventContent={(arg) => {
            const hasConflict = arg.event.extendedProps?.hasConflict;
            const hasInventory = arg.event.extendedProps?.hasInventory;
            return (
              <div className="flex items-center gap-1 min-w-0 w-full px-0.5">
                {hasConflict && <span className="flex-shrink-0 text-[11px] leading-none">⚠</span>}
                {hasInventory && <span className="flex-shrink-0 text-[10px] leading-none" title="Inventario emesso">📦</span>}
                <span className="fc-event-title truncate" title={arg.event.title}>{arg.event.title}</span>
              </div>
            );
          }}
          eventOrder={displayMode === "span" ? "start,-end" : "sortOrder"}
          datesSet={(arg) => setTitle(arg.view.title)}
          height="auto" dayMaxEvents={displayMode === "span" ? false : 3} eventDisplay="block"
          eventTimeFormat={FC_EVENT_TIME_FORMAT}
          buttonText={FC_BUTTON_TEXT}
        />
        </div>

        <ConflictsPanel
          allConflictDocs={allConflictDocs}
          conflictDocs={conflictDocs}
          hiddenByFilter={hiddenByFilter}
          operatorOptions={operatorOptions}
          operatorFilter={operatorFilter}
          setOperatorFilter={setOperatorFilter}
          onlyMine={onlyMine}
          setOnlyMine={setOnlyMine}
          onOpenDoc={openConflictDoc}
        />
      </div>

      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        canEdit={canEdit}
        onRescheduleRelease={async ({ proformaId, oldDate, newDate }) => {
          let monthlyCost = 0;
          try {
            const pfRes = await api.get(`/proformas/${proformaId}`);
            const qid = pfRes.data?.quote_id;
            if (qid) {
              const qRes = await api.get(`/quotes/${qid}`);
              monthlyCost = qRes.data?.storage_monthly_cost || 0;
            }
          } catch (err) {
            if (process.env.NODE_ENV !== "production") {
              console.warn("[Calendar] Impossibile recuperare il monthly_cost del preventivo:", err?.message);
            }
          }
          // Riusa lo stesso flusso di drag&drop, senza l'oggetto info (no revert necessario)
          setExtendRelease({ proformaId, oldDate, newDate, monthlyCost, info: null });
        }}
      />

      <ClusterPickerDialog
        clusterPicker={clusterPicker}
        onClose={() => setClusterPicker(null)}
        onSelect={(doc) => {
          setClusterPicker(null);
          navigate(doc.doc_type === "inventory" ? `/inventories/${doc.doc_id}` : `/quotes/${doc.doc_id}`);
        }}
      />

      <ExtendReleaseDialog
        open={!!extendRelease}
        oldDate={extendRelease?.oldDate}
        newDate={extendRelease?.newDate}
        monthlyCost={extendRelease?.monthlyCost}
        loading={extendLoading}
        onConfirm={confirmExtendRelease}
        onCancel={cancelExtendRelease}
      />
    </div>
  );
}
